import {
  FC, type JSX, useMemo, useState,
} from 'react';

import { FilterAndSearchRow } from './FilterAndSearchRow';
import { SearchableTableContent } from './SearchableTableContent';
import { ActiveFilters, ColumnDefinition, FilterGroup } from './types';

type SearchableTableProps<T> = {
  initialData: T[];
  searchFunction: (query: string, data: T) => boolean;
  rowComponent: (item: T) => JSX.Element;
  columns: ColumnDefinition[];
  filterGroups?: FilterGroup<T>[];
  hideSearch?: boolean; // hide search bar
  useDropdown?: boolean; // Whether to use dropdown instead of filter buttons
};

// Filter and search helper function
function filterAndSearch<T>(
  data: T[],
  groups: FilterGroup<T>[],
  filters: ActiveFilters,
  searchQuery: string,
  searchFunction: (query: string, data: T) => boolean,
) {
  const filteredData = data.filter((item) => {
    return Object.keys(filters).every((groupId) => {
      const currentGroup = groups.find((f) => f.groupId === groupId);
      const currentFilter = currentGroup?.filters.find((f) => f.name === filters[groupId]);
      return currentFilter ? currentFilter.applyFilterFunction(item) : true;
    });
  });

  return filteredData.filter((item) => searchFunction(searchQuery, item));
}

const SearchableTable: FC<SearchableTableProps<any>> = ({
  initialData,
  searchFunction,
  rowComponent,
  columns,
  filterGroups = [],
  hideSearch = false,
  useDropdown = false,
}) => {
  // State for search query
  const [query, setQuery] = useState('');

  // State for active filters
  const [activeFilters, setActiveFilters] = useState<ActiveFilters>(() => filterGroups.reduce(
    (acc: ActiveFilters, { groupId, filters }) => {
      acc[groupId] = filters[0]?.name || null;
      return acc;
    },
    {},
  ));

  // Update active filters when a filter button is clicked
  const handleFilterChange = (groupId: string, filterName: string) => {
    setActiveFilters((prevFilters) => ({ ...prevFilters, [groupId]: filterName }));
  };

  // Filtered and searched data for the table
  const filteredData = useMemo(
    () => filterAndSearch(initialData, filterGroups, activeFilters, query, searchFunction),
    [initialData, filterGroups, activeFilters, query, searchFunction],
  );

  return (
    <div className="flex flex-col">
      <FilterAndSearchRow
        filterGroups={filterGroups}
        activeFilters={activeFilters}
        handleFilterChange={handleFilterChange}
        query={query}
        setQuery={setQuery}
        hideSearch={hideSearch}
        useDropdown={useDropdown}
      />
      <SearchableTableContent
        filteredData={filteredData}
        rowComponent={rowComponent}
        columns={columns}
      />
    </div>
  );
};

export default SearchableTable;
